import React from 'react';
import PropTypes from 'prop-types';
import { TextField, Grid, Button } from '@mui/material';
import { withTheme } from '@mui/styles';

const initialTemplate = `{
    "method": "public/test",
    "params": {
        
    },
    "jsonrpc": "2.0",
    "id": 1
}
`;

class RequestRawInput extends React.PureComponent {
	constructor(props) {
		super(props);

		this.state = {
			requestInput: initialTemplate,
			error: false,
			helperText: '',
			observer: undefined,
		};

		this.onChange = this.onChange.bind(this);
	}

	componentDidMount() {
		this.init();
	}

	componentWillUnmount() {
		this.state.observer?.disconnect();
	}

	init() {
		if (this.alreadyBuilt !== true) {
			this.alreadyBuilt = true;
			let url = new URL(window.location.href);
			let method = url.searchParams.get('method');
			url.searchParams.delete('n');
			if (method) {
				method = method.replace(/^\/+/, '');
				url.searchParams.delete('method');
				let params = {};
				url.searchParams.forEach((value, name) => {
					if (value.includes(',')) {
						params[name] = value.split(',').filter(Boolean);
					} else {
						params[name] = value;
					}
				});
				this.setState({ requestInput: JSON.stringify({ method: method, params: params }, null, 2) });
			}
		}
	}

	onChange(event) {
		this.setState({ requestInput: event.target.value });
	}

	getRequest() {
		try {
			let request = JSON.parse(this.state.requestInput);
			if (request && typeof request === 'object' && request.constructor === Object && request.method) {
				if (this.state.error) {
					this.setState({ error: false, helperText: '' });
				}
				return request;
			} else {
				this.setState({ error: true, helperText: 'Invalid input.' });
				return null;
			}
		} catch (e) {
			this.setState({ error: true, helperText: `Invalid input. ${e.message || ''}` });
			return null;
		}
	}

	send(type) {
		let request = this.getRequest();
		if (request) {
			this.props.onSend(this.getRequest(), type);
		}
	}

	setObserver = (inputRef) => {
		if (inputRef) {
			const requestContainerRef = document.querySelector('#request-container');
			const buttonsContainerRef = document.querySelector('#buttons-container');
			const helperTextRef = document.querySelector('#info-text');
			
			const observer = new ResizeObserver(() => {
				const buttonsHeight = buttonsContainerRef.getBoundingClientRect().height;
				const requestContainerHeight = requestContainerRef.getBoundingClientRect().height;
				const helperTextHeight = helperTextRef?.getBoundingClientRect().height;
				const maxRows = Math.floor((requestContainerHeight - buttonsHeight - helperTextHeight - 2*8 - 2*10 - 10 - 2*16.5) / 19);
				this.setState({maxRows});
			});
			observer.observe(inputRef);
			this.setState({observer});
		}
	};


	render() {
		return (
			<div style={{ display: 'flex', flexFlow: 'column', height: '100%', overflow: 'hidden', padding: 8, gap: '8px', background: this.props.theme.palette.background.paper }} id="request-container">
				<div style={{ flex: '1 1 auto' }}>
					<TextField
						ref={this.setObserver}
						type={'text'}
						value={this.state.requestInput}
						label="Request"
						onChange={this.onChange}
						onBlur={this.onChange}
						multiline={true}
						margin="dense"
						variant="outlined"
						fullWidth
						sx={{
							height: '100%',
							'& .MuiInputBase-root': {
								height: 'calc(100% - 8px)',
								fontFamily: 'monospace', 
								fontSize: '13px',
								alignItems: 'start',
								lineHeight: '19px'
							},
							'& .MuiFormHelperText-root': {
								textWrap: 'wrap'
							}
						}}
						error={this.state.error}
						InputLabelProps={{
							shrink: true,
						}}
						tabIndex="0"
						onKeyDown={handleTab}
						maxRows={this.state.maxRows}
					/>
				</div>
				<div style={{flex: '0 1 auto', color: '#da5750', fontSize: '12px'}} id="info-text">{this.state.helperText}</div>
				<div style={{ flex: '0 1 auto'}} id="buttons-container">
					<Grid container direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
						<Grid item><Button size="small" onClick={() => this.setState({ requestInput: '' })}>Clear</Button></Grid>
						<Grid item><Button size="small" onClick={() => this.setState({ requestInput: initialTemplate })}>Reset</Button></Grid>
						<Grid item><Button size="small" variant="contained" color="primary" onClick={() => this.send('WS')}>Ws</Button></Grid>
						<Grid item><Button size="small" variant="contained" color="primary" onClick={() => this.send('GET')}>Get</Button></Grid>
						<Grid item><Button size="small" variant="contained" color="primary" onClick={() => this.send('POST')}>Post</Button></Grid>
					</Grid>
				</div>
			</div>
		);
	}
}

RequestRawInput.propTypes = {
	onSend: PropTypes.func,
	theme: PropTypes.object,
};

function handleTab(e) {
	if (e.key === 'Tab') {
		let target = e.target,

			value = target.value,
			start = target.selectionStart,
			end = target.selectionEnd,

			oldStart = start,
			oldEnd = end,
			replacement = '',

			startDelta = 0;

		if(!e.shiftKey) {
			let sub = value.substring(start, end);
			if(start === end || sub.search(/(\r(\n)?|\n)/g) === -1) { // no selection or just in one line
				replacement = '\t'; // add (or replace as) one tab
			} else {
				// "real" start for good line intendation
				start = findFirstLineBreakBefore(value, start);
				replacement = (start === 0 ? '\t' : '') + value.substring(start, end).replace(/(\r(\n)?|\n)[^$]/g, '$1\t'); // add tabs
			}
		} else {
			if(start === end && (start === 0 || value[start - 1] === '\n' || value[start - 1] === '\n')) {
				end += 1;
			}
			start = findFirstLineBreakBefore(value, start); // start is always beginning of line
			replacement = value.substring(start, end).replace(/(^\t+)|((\r(\n)?|\n)\t+)/g, m => m.slice(0, -1));
			startDelta = -1;
		}

		let newValue = value.substring(0, start) + replacement + value.substring(end);
		if(newValue !== value) {
			target.value = newValue;

			target.selectionEnd = start + replacement.length;

			if(oldStart === oldEnd) { // no selection
				target.selectionStart = target.selectionEnd;
			} else {
				let c = newValue[oldStart + startDelta];
				target.selectionStart = ((startDelta < 0) && (c !== '\n') && (c !== '\r')) || (startDelta > 0) ? Math.max(oldStart + startDelta, 0) : oldStart;
			}
		}

		e.preventDefault();
	}
}

function findFirstLineBreakBefore(string, startIdx=0) {
	let currIdx = startIdx - 1;
	while(currIdx >= 0) {
		let c = string[currIdx];
		if(c === '\n' || c === '\r') {
			return currIdx;
		}
		currIdx -= 1;
	}
	return 0;
}

export default withTheme(RequestRawInput);
